import { DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';

// Deliberately not a translation key: every tenant build keeps the SquadBox
// brand in the tab, whatever its i18n overrides rename.
export const PAGE_TITLE_SUFFIX = 'SquadBox';
const PAGE_TITLE_SEPARATOR = ' · ';

/** `My Skills` → `My Skills · SquadBox`; nothing → `SquadBox`. */
export const formatPageTitle = (title: string | null | undefined): string => {
  const trimmed = title?.trim();

  return trimmed
    ? `${trimmed}${PAGE_TITLE_SEPARATOR}${PAGE_TITLE_SUFFIX}`
    : PAGE_TITLE_SUFFIX;
};

/**
 * Owns `document.title`. The tab names the most specific thing on screen:
 *
 * 1. an entity title — the chat, skill or teammate the active page shows,
 *    pushed by that page through `bindPageTitle()` and kept live, so a
 *    generated chat title or a rename lands in the tab as it lands in the store;
 * 2. otherwise the active route's `title` (an i18n key, set by
 *    `PageTitleStrategy` on every navigation) — `My Skills`, `Teammates`, …;
 * 3. otherwise just the suffix.
 */
@Injectable({ providedIn: 'root' })
export class PageTitleService {
  private readonly title = inject(Title);
  private readonly translateService = inject(TranslateService);

  private readonly routeTitle = signal<string | undefined>(undefined);

  /**
   * Tagged with the page instance that set it, so a page being torn down can
   * only clear its own title — never the one the next page already pushed.
   */
  private readonly entityTitle = signal<{
    owner: object;
    title: string | undefined;
  } | null>(null);

  constructor() {
    effect(() => {
      this.title.setTitle(
        formatPageTitle(this.entityTitle()?.title ?? this.routeTitle()),
      );
    });
  }

  /**
   * Translated once, on navigation. Nothing re-translates it in place because
   * nothing needs to: translations load before the first navigation
   * (`provideTranslationInitializer`), and switching language reloads the app
   * (`setLanguageInLocalStorageAndReload`).
   */
  public setRouteTitleKey(key: string | undefined): void {
    this.routeTitle.set(
      key ? (this.translateService.instant(key) as string) : undefined,
    );
  }

  public setEntityTitle(owner: object, title: string | undefined): void {
    this.entityTitle.set({ owner, title });
  }

  public clearEntityTitle(owner: object): void {
    if (this.entityTitle()?.owner === owner) {
      this.entityTitle.set(null);
    }
  }
}

/**
 * Names the tab after the entity the calling page shows, for as long as the
 * page lives. `title` is read reactively; return `undefined` while the entity
 * is not resolved yet to fall back to the route's title.
 *
 * Must run in an injection context (a component field initializer or
 * constructor).
 */
export function bindPageTitle(title: () => string | null | undefined): void {
  const pageTitleService = inject(PageTitleService);
  const owner = {};

  effect(() => {
    pageTitleService.setEntityTitle(owner, title() ?? undefined);
  });
  inject(DestroyRef).onDestroy(() => {
    pageTitleService.clearEntityTitle(owner);
  });
}
